// src/components/avatar/FocusAvatar.js
//
// An avatar you can TAP to see the whole body. Only one avatar on the page
// is ever full-body: tapping another switches, tapping the same one goes back
// to the portrait. Nothing is saved, so a refresh shows portraits again.
//
//   <FocusAvatar id={uid} look={...} outfit={...} size={38} fullSize={170} />
//   <FocusAvatar id={uid} size={56} />        — loads the look itself
//   <FocusAvatar id="detail-x" uid={uid} … />  — a second copy of the same
//        student with its own on/off (id = which one, uid = whose look)
//
// Used by the leaderboards and the Classroom.

import React, { useEffect, useState } from "react";
import AvatarWithOutfit from "./AvatarWithOutfit";
import { useAvatarLooks } from "../../services/avatarService";

// one shared "which avatar is full body" for the whole page
let focused = null;
const listeners = new Set();
const setFocused = (id) => {
  focused = id;
  listeners.forEach((f) => f(id));
};
export const useFocusedAvatar = () => {
  const [id, setId] = useState(focused);
  useEffect(() => {
    listeners.add(setId);
    return () => listeners.delete(setId);
  }, []);
  return id;
};
export const clearFocusedAvatar = () => setFocused(null);

const Loaded = ({ id, uid, ...rest }) => {
  const key = uid || id;
  const map = useAvatarLooks(key ? [key] : []);
  const who = map[key] || {};
  return <Shown id={id} look={who.look || null} outfit={who.outfit || {}} {...rest} />;
};

const Shown = ({ id, look, outfit, size, fullSize, className = "", name = "" }) => {
  const current = useFocusedAvatar();
  const full = Boolean(id) && current === id;
  return (
    <AvatarWithOutfit
      look={look}
      outfit={outfit}
      size={full ? fullSize || Math.max(150, size * 2.4) : size}
      full={full}
      className={`sbl-focus-av ${full ? "is-focused" : ""} ${className}`}
      title={full ? `Tap to go back to ${name ? `${name}'s ` : ""}portrait` : `Tap to see ${name ? `${name}'s` : "the"} whole avatar`}
      onClick={(e) => {
        e.stopPropagation();
        setFocused(full ? null : id);
      }}
    />
  );
};

const FocusAvatar = ({ id, uid, look, outfit, ...rest }) =>
  look === undefined ? <Loaded id={id} uid={uid} {...rest} /> : <Shown id={id} look={look} outfit={outfit || {}} {...rest} />;

export default FocusAvatar;
